"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../@/components/ui/table";
import { useState } from "react";
import Loader from "./LoaderComponent";
import { USER_ROLE } from "../lib/constants";
import { IsServiceTypeVendor, tokenDecoder } from "../lib/utils";
import { useSelector } from "react-redux";
import InfoTooltip from "./common/InfoTooltip";
import VendorAdminTimeSlot from "./VendorAdminTimeSlot";
import OrderSummarySkeleton from "./OrderSummarySkeleton";

interface OrderItems {
  id: number;
  quantity: number;
  defaultImageUrl: string;
  productName: string;
  price: number;
  modeId: number;
}

interface SummaryPageProps {
  orders: OrderItems[];
  deliveryFee?: number | null;
  totalAmount?: number | null;
  serviceFee?: number | null;
  paymentProcessingFee?: number | null;
  oldTotalAmount?: number | null;
  oldDeliveryCharges?: number | null;
  bookedVendorProductTimeSlot?: {
    id: number;
    startTime: string;
    endTime: string;
    bookingDate: string;
    calenderLink: string;
    googleMeetLink: string;
    latitude: number;
    longitude: number;
  };
}

interface CustomColumnMeta {
  className?: string;
}

type CustomColumnDef<TData> = ColumnDef<TData, unknown> & {
  meta?: CustomColumnMeta;
};

const calculateProductTotal = (quantity: number, price: number) => {
  const productQuantity = quantity || 1;
  const unitPrice = parseFloat(price.toFixed(2)) || 0;
  return (productQuantity * unitPrice).toFixed(2);
};

const getCollectiveSubtotal = (
  orders: OrderItems[],
  deliveryFee?: number | null
) => {
  let subTotal = 0;
  for (let i = 0; i < orders.length; i++) {
    const order = orders[i];
    subTotal += parseFloat(calculateProductTotal(order.quantity, order.price));
  }
  if (deliveryFee) {
    subTotal += deliveryFee;
  }
  return parseFloat(subTotal.toFixed(2));
};

const getTotalItems = (orders: OrderItems[]) => {
  let totalItems = 0;
  for (let i in orders) {
    totalItems++;
  }
  return totalItems;
};

export const orderColumns: CustomColumnDef<OrderItems>[] = [
  {
    accessorKey: "productName",
    header: () => (
      <div className="w-2/5">
        {IsServiceTypeVendor() ? "Service Name" : "Product Name"}
      </div>
    ),
    meta: { className: "w-2/5" },
    cell: ({ row }) => (
      <div className="flex items-center">
        <img
          src={row.original.defaultImageUrl}
          alt={row.getValue("productName")}
          className="w-10 h-10 hidden sm:block mr-2 rounded"
        />
        <div className="overflow-hidden text-ellipsis">
          {row.getValue("productName")}
        </div>
      </div>
    ),
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    meta: { className: "w-1/8 text-center" },
    cell: ({ row }) => <div>{row.getValue("quantity")}</div>,
  },
  {
    accessorKey: "price",
    header: "Unit Price",
    meta: { className: "w-1/8 text-center" },
    cell: ({ row }) => {
      const price = parseFloat((row.getValue("price") as number).toFixed(2));
      return (
        <div>
          {price.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </div>
      );
    },
  },
  {
    accessorKey: "total",
    header: "Total",
    meta: { className: "w-1/8 text-center" },
    cell: ({ row }) => {
      const quantity = row.getValue("quantity");
      const price = row.getValue("price");
      const total = calculateProductTotal(quantity as number, price as number);
      return (
        <div>
          {parseFloat(total).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </div>
      );
    },
  },
];
export function OrdersTable({ orders }: { orders: OrderItems[] }) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 3,
  });

  const table = useReactTable({
    data: orders,
    columns: orderColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  return (
    <div className="w-full">
      <div className="border-y overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={`${
                      header.column.id === "id" ? "hidden xs:table-cell" : ""
                    } ${
                      (header.column.columnDef as CustomColumnDef<OrderItems>)
                        .meta?.className || ""
                    } bg-customTableBg text-customGrayBase`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={`${
                        cell.column.id === "id" ? "hidden xs:table-cell" : ""
                      } ${
                        (cell.column.columnDef as CustomColumnDef<OrderItems>)
                          .meta?.className || ""
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={orderColumns.length}
                  className="h-24 text-center"
                >
                  <div className="space-y-4">
                    {[1].map((index) => (
                      <div key={index} className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-200 rounded animate-pulse" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
                        </div>
                        <div className="w-20 h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="w-20 h-4 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between space-x-6 mt-4">
        <button
          onClick={() =>
            setPagination((prev) => ({
              ...prev,
              pageIndex: Math.max(prev.pageIndex - 1, 0),
            }))
          }
          disabled={pagination.pageIndex === 0}
          className="bg-customOffWhiteColor border rounded-md text-customGrayDark px-4 py-2"
        >
          Previous
        </button>
        <button
          onClick={() =>
            setPagination((prev) => ({
              ...prev,
              pageIndex: prev.pageIndex + 1,
            }))
          }
          disabled={pagination.pageIndex >= table.getPageCount() - 1}
          className={`border rounded-md text-white px-4 py-2 ${
            pagination.pageIndex >= table.getPageCount() - 1
              ? "bg-customGreenDisabled"
              : "bg-customGreenColor"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
export function SummaryTable({
  orders,
  deliveryFee,
  totalAmount = 0,
  serviceFee = 0,
  paymentProcessingFee = 0,
  oldTotalAmount = 0,
  oldDeliveryCharges = 0,
}: {
  orders: OrderItems[];
  deliveryFee?: number | null;
  totalAmount?: number | null;
  serviceFee?: number | null;
  paymentProcessingFee?: number | null;
  oldTotalAmount?: number | null;
  oldDeliveryCharges?: number | null;
}) {
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const subtotal = getCollectiveSubtotal(orders);
  const estimatedTotal = totalAmount;
  const items = getTotalItems(orders);

  return (
    <div className="w-full lg:max-w-xs mt-4 ml-auto border-y">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              colSpan={2}
              className="text-left text-customGrayBase bg-customTableBg font-semibold"
            >
              Summary
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <div>
              <TableHead className="justify-start">
                Subtotal ({items} items)
              </TableHead>
            </div>
            <TableHead className="text-right">${subtotal.toFixed(2)}</TableHead>
          </TableRow>

          <TableRow>
            <TableHead className="flex items-center">
              <div className="flex items-center">
                Service Fee
                <InfoTooltip content="This fee supports the Afrocart platform and covers various operating costs - including insurance, background checks, and customer support." />
              </div>
            </TableHead>
            <TableHead className="text-right">
              {decodedToken.role == USER_ROLE.ADMIN ? "" : "-"} $
              {serviceFee?.toFixed(2) || "0.00"}
            </TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="flex items-center ">
              <div className="flex items-center">
                Transaction Fee
                <InfoTooltip content="A transaction fee is applied by payment service to process each payment securely. This fee covers payment handling, fraud protection, and card processing services." />
              </div>
            </TableHead>
            <TableHead className="text-right">
              - ${paymentProcessingFee?.toFixed(2) || "0.00"}
            </TableHead>
          </TableRow>

          {decodedToken.role == USER_ROLE.ADMIN && (
            <TableRow>
              <div>
                <TableHead>Delivery Fee</TableHead>
              </div>

              <TableHead className="text-right">
                {oldDeliveryCharges ? (
                  <>
                    <span className="mr-2 line-through text-red-500 text-[10px]">
                      ${oldDeliveryCharges?.toFixed(2)}
                    </span>
                    <span className="text-customGreenColor">
                      ${deliveryFee?.toFixed(2) || "0.00"}
                    </span>
                  </>
                ) : (
                  `$${deliveryFee?.toFixed(2) || "0.00"}`
                )}
              </TableHead>
            </TableRow>
          )}

          <TableRow>
            <div>
              <TableHead className="font-semibold">Estimated Total</TableHead>
            </div>
            <TableHead className="text-right font-semibold">
              {oldTotalAmount ? (
                <>
                  <span className="mr-2 line-through text-red-500 text-[10px]">
                    ${oldTotalAmount?.toFixed(2)}
                  </span>
                  <span className="text-customGreenColor">
                    ${estimatedTotal?.toFixed(2)}
                  </span>
                </>
              ) : (
                `$${estimatedTotal?.toFixed(2)}`
              )}
            </TableHead>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

const OrderSummaryPage: React.FC<SummaryPageProps> = ({
  orders = [],
  deliveryFee,
  totalAmount = 0,
  serviceFee = 0,
  paymentProcessingFee = 0,
  oldTotalAmount = 0,
  oldDeliveryCharges = 0,
  bookedVendorProductTimeSlot,
}) => {
  if (!orders.length) {
    return <OrderSummarySkeleton />;
  }

  return (
    <div className="p-7 space-y-2">
      <OrdersTable orders={orders} />
      <div className="flex flex-col lg:flex-row space-x-5 justify-between">
        {bookedVendorProductTimeSlot && IsServiceTypeVendor() && (
          <VendorAdminTimeSlot
            productModeId={orders[0].modeId}
            startTime={bookedVendorProductTimeSlot.startTime}
            endTime={bookedVendorProductTimeSlot.endTime}
            bookingDate={bookedVendorProductTimeSlot.bookingDate}
            calenderLink={bookedVendorProductTimeSlot.calenderLink}
            googleMeetLink={bookedVendorProductTimeSlot.googleMeetLink}
            latitude={bookedVendorProductTimeSlot.latitude}
            longitude={bookedVendorProductTimeSlot.longitude}
          />
        )}
        <SummaryTable
          orders={orders}
          deliveryFee={deliveryFee}
          serviceFee={serviceFee}
          totalAmount={totalAmount}
          paymentProcessingFee={paymentProcessingFee}
          oldTotalAmount={oldTotalAmount}
          oldDeliveryCharges={oldDeliveryCharges}
        />
      </div>
    </div>
  );
};
export default OrderSummaryPage;
